// api/assistantClient.js
import axios from 'axios';
import TokenService from '../services/token.service';

/**
 * Cliente HTTP configurado para Assistant Service
 * Base URL: Configurado desde variable de entorno
 */
const assistantClient = axios.create({
  baseURL: process.env.REACT_APP_ASSISTANT_API_URL,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Interceptor para agregar JWT token automáticamente
 */
assistantClient.interceptors.request.use(
  (config) => {
    const token = TokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Interceptor para manejar errores de respuesta
 */
assistantClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Token expirado o inválido
    if (error.response?.status === 401) {
      console.error('❌ Token inválido o expirado');
      // Limpiar tokens y redirigir a login
      TokenService.clearTokens();
      window.location.href = '/login';
    }

    // Log de errores para debugging
    if (error.response) {
      console.error('API Error:', {
        status: error.response.status,
        data: error.response.data,
        endpoint: error.config?.url,
      });
    } else if (error.request) {
      console.error('No response from server:', error.request);
    } else {
      console.error('Request setup error:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Helper para manejar errores de API
 */
export const handleApiError = (error) => {
  if (error.response) {
    const status = error.response.status;
    // Algunos servicios (p. ej. image_service) devuelven detail como objeto
    // { code, message }; otros como string. Normalizamos a un string legible.
    const rawDetail = error.response.data?.detail;
    const detail =
      rawDetail && typeof rawDetail === 'object'
        ? rawDetail.message || rawDetail.code
        : rawDetail || error.response.data?.message;

    const errorMap = {
      400: 'Bad Request - Datos inválidos',
      401: 'No autorizado - Token inválido o expirado',
      403: 'Prohibido - No tienes permisos',
      404: 'No encontrado - Recurso no existe',
      422: 'Error de validación',
      500: 'Error del servidor',
    };

    return {
      status,
      message: detail || errorMap[status] || 'Error desconocido',
      errors: error.response.data?.errors || null,
    };
  } else if (error.request) {
    return {
      status: 0,
      message: 'No se pudo conectar con el servidor',
      errors: null,
    };
  } else {
    return {
      status: -1,
      message: error.message || 'Error desconocido',
      errors: null,
    };
  }
};

export default assistantClient;
